import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, HelpCircle, Sparkles } from "lucide-react";

/**
 * App-wide styled confirm/prompt/alert dialog.
 *
 * Native window.confirm / window.prompt look out of place next to the chunky
 * 67Cafe UI and can't be styled. This provider exposes a hook that returns
 * Promise-based confirm/prompt/alert helpers — same ergonomics as the
 * browser primitives, but rendered as a bordered chunky modal.
 *
 *   const { confirm, prompt, alert } = useAppDialog();
 *   if (!(await confirm({ title: "Sign out?" }))) return;
 *   const answer = await prompt({ title: "Pick 1-3", defaultValue: "2" });
 */

const AppDialogContext = createContext(null);

const TONE_STYLES = {
  info:    { icon: HelpCircle,      bg: "#EFE7FF", text: "#5F27CD", btn: "btn-purple" },
  warning: { icon: AlertTriangle,   bg: "#FFF3D6", text: "#B87500", btn: "btn-purple" },
  danger:  { icon: AlertTriangle,   bg: "#FFE9E9", text: "#B00020", btn: "btn-danger" },
  success: { icon: Sparkles,        bg: "#E9FFE6", text: "#136B22", btn: "btn-purple" },
};

export const AppDialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const resolverRef = useRef(null);
  const inputRef = useRef(null);

  const closeWith = useCallback((value) => {
    if (resolverRef.current) resolverRef.current(value);
    resolverRef.current = null;
    setDialog(null);
    setInputValue("");
  }, []);

  const open = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setInputValue(opts.defaultValue ?? "");
      setDialog(opts);
    });
  }, []);

  const api = useMemo(() => ({
    confirm: (opts = {}) => open({ ...opts, kind: "confirm" }),
    prompt: (opts = {}) => open({ ...opts, kind: "prompt" }),
    alert: (opts = {}) => open({ ...opts, kind: "alert" }),
  }), [open]);

  // Autofocus the input on prompt mount.
  useEffect(() => {
    if (dialog?.kind === "prompt") {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [dialog]);

  // Esc cancels, Enter confirms.
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeWith(dialog.kind === "prompt" ? null : false);
      }
      if (e.key === "Enter" && dialog.kind !== "prompt") {
        // Prompts have their own form-submit path so Enter picks up the input.
        e.preventDefault();
        closeWith(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, closeWith]);

  const tone = TONE_STYLES[dialog?.tone || "info"];
  const Icon = tone?.icon || HelpCircle;
  const kind = dialog?.kind;

  const submitPrompt = (e) => {
    e?.preventDefault?.();
    closeWith(inputValue);
  };

  return (
    <AppDialogContext.Provider value={api}>
      {children}
      {dialog && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          data-testid="app-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="app-dialog-title"
          style={{
            background: "rgba(30, 20, 55, 0.55)",
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeWith(kind === "prompt" ? null : false);
            }
          }}
        >
          <div
            className="chunky-card p-5 sm:p-6 w-full max-w-sm overflow-y-auto"
            style={{
              background: "#FFF9E6",
              animation: "chunkyPop 180ms ease-out",
              maxHeight: "85vh",
            }}
          >
            <div className="flex items-start gap-3 mb-2">
              <span
                className="inline-flex items-center justify-center rounded-full"
                style={{
                  width: 44,
                  height: 44,
                  background: tone.bg,
                  border: "3px solid #2b2b2b",
                  color: tone.text,
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <Icon size={22} strokeWidth={3} />
              </span>
              <div className="flex-1 min-w-0">
                <h2
                  id="app-dialog-title"
                  className="font-display text-lg leading-tight"
                  data-testid="app-dialog-title"
                >
                  {dialog.title || "Are you sure?"}
                </h2>
                {dialog.message && (
                  <p
                    className="text-sm mt-1 whitespace-pre-line"
                    style={{ color: "#484848" }}
                    data-testid="app-dialog-message"
                  >
                    {dialog.message}
                  </p>
                )}
              </div>
            </div>

            {kind === "prompt" && (
              <form onSubmit={submitPrompt}>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={dialog.placeholder || ""}
                  data-testid="app-dialog-input"
                  className="w-full mt-3 px-3 py-2 font-display"
                  style={{
                    border: "3px solid #2b2b2b",
                    borderRadius: 12,
                    background: "#fff",
                    outline: "none",
                  }}
                />
              </form>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
              {kind !== "alert" && (
                <button
                  type="button"
                  onClick={() => closeWith(kind === "prompt" ? null : false)}
                  data-testid="app-dialog-cancel"
                  className="btn-3d btn-sm"
                >
                  {dialog.cancelLabel || "Cancel"}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (kind === "prompt") submitPrompt();
                  else closeWith(true);
                }}
                data-testid="app-dialog-confirm"
                className={`btn-3d btn-sm ${tone.btn}`}
              >
                {dialog.confirmLabel || (kind === "alert" ? "OK" : "Yes, do it")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppDialogContext.Provider>
  );
};

/**
 * Access the app dialog helpers from any component within AppDialogProvider.
 * Safe fallback to native APIs when the provider is missing (during tests
 * that mount a component in isolation).
 */
export const useAppDialog = () => {
  const ctx = useContext(AppDialogContext);
  return ctx || {
    confirm: async (opts) => window.confirm(opts?.message || opts?.title || "Are you sure?"),
    prompt: async (opts) => window.prompt(opts?.message || opts?.title || "", opts?.defaultValue ?? ""),
    alert: async (opts) => { window.alert(opts?.message || opts?.title || ""); return true; },
  };
};
